/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, May 2020
 */

import React, { useMemo } from 'react';
import defaults from 'lodash.defaults';
import pick from 'lodash.pick';
import {
  Choice,
  Question,
  FlowBlockQuestion,
  questionObjExtraParams,
} from '@staffino/model';
import ChoiceQuestionSingleSelectFragment from './fragments/choice-question-single-select-fragment';
import ChoiceQuestionDropdownFragment from './fragments/choice-question-dropdown-fragment';
import ChoiceQuestionMultiSelectFragment from './fragments/choice-question-multi-select-fragment';

type Props = {
  block: FlowBlockQuestion;
  question: Question;
  choices: Choice[];
};

function ChoiceQuestionBlock({ block, question, choices = [] }: Props) {
  const required = (block as any).required ?? question.required; // for backward compatibility
  const extraParams = defaults(
    pick(question.extraParams, questionObjExtraParams),
    pick(block, questionObjExtraParams),
  );
  const titleSize = question.extraParams?.titleSize ?? (block as any).titleSize;

  const ordinaryChoices = useMemo(
    () =>
      choices.filter(
        (choice) => choice.id !== block.otherChoiceId && !choice.isOther,
      ),
    [choices, block.otherChoiceId],
  );
  const otherChoice = useMemo(
    () =>
      choices.find(
        (choice) => choice.id === block.otherChoiceId || choice.isOther,
      ),
    [choices, block.otherChoiceId],
  );

  const orderedChoices = useMemo(() => {
    const sortedChoices = ordinaryChoices
      .sort((a, b) => a.sorting - b.sorting)
      .filter((choice) => !!choice.title);

    if (!extraParams?.shuffle) {
      return sortedChoices;
    }
    const shuffledChoices = sortedChoices
      .map<[number, Choice]>((choice) => [Math.random(), choice])
      .sort((a, b) => a[0] - b[0])
      .map(([, choice]) => choice);

    const needToPin =
      !otherChoice && extraParams?.pinLastOption && shuffledChoices.length > 1;
    if (!needToPin) {
      return shuffledChoices;
    }
    const lastChoice = sortedChoices[shuffledChoices.length - 1];
    return [
      ...shuffledChoices.filter((choice) => choice.id !== lastChoice.id),
      lastChoice,
    ];
  }, [
    extraParams?.shuffle,
    extraParams?.pinLastOption,
    ordinaryChoices,
    otherChoice,
  ]);

  function isDefaultSkin(skin: string | any) {
    if (!skin || ['default', 'list', 'List'].includes(skin)) {
      return true;
    }
    if (typeof skin === 'object' && 'type' in skin) {
      return isDefaultSkin(skin.type);
    }
    return false;
  }

  function isDropdownSkin(skin: string | any) {
    if (!skin) {
      return false;
    }
    if (['dropdown', 'Dropdown'].includes(skin)) {
      return true;
    }
    if (typeof skin === 'object' && 'type' in skin) {
      return isDropdownSkin(skin.type);
    }
    return false;
  }

  return (
    <>
      {question.maxChoiceCount > 1 && (
        <ChoiceQuestionMultiSelectFragment
          blockId={block.id}
          question={question}
          choices={orderedChoices}
          otherChoice={otherChoice}
          titleSize={titleSize}
          limit={extraParams?.limit}
          minChoiceLimit={extraParams?.minChoiceLimit}
          maxChoiceLimit={extraParams?.maxChoiceLimit}
          required={required}
          colorStyle={question.colorStyle ?? undefined}
        />
      )}

      {question.maxChoiceCount === 1 && isDefaultSkin(question.skin) && (
        <ChoiceQuestionSingleSelectFragment
          blockId={block.id}
          question={question}
          choices={orderedChoices}
          otherChoice={otherChoice}
          titleSize={titleSize}
          limit={extraParams?.limit}
          required={required}
          colorStyle={question.colorStyle ?? undefined}
        />
      )}

      {question.maxChoiceCount === 1 && isDropdownSkin(question.skin) && (
        <ChoiceQuestionDropdownFragment
          blockId={block.id}
          question={question}
          choices={orderedChoices}
          otherChoice={otherChoice}
          placeholder={question.placeholder ?? (block as any).placeholder}
          titleSize={titleSize}
          commentLimit={extraParams?.limit}
          required={required}
        />
      )}
    </>
  );
}

export default ChoiceQuestionBlock;
